/**
 * 信息流（`#info-flow`）：大模型节点（信号、排序、N2、N3）的每块输入从哪来、谁组装、什么条件给、护栏、走不走字段契约（总节奏第 1 期）。
 * 组装出没登记的键会在运行留痕里告警（FLOW-REG）——渲染只取登记过的键，没登记的会被丢掉。
 * 只读，改登记改 plan_lab/info_flow.py，走 PR。
 */
import { useMemo, useState } from "react";

import { Card, CardContent } from "@/components/ui/card";
import { EmptyState, LoadingInline } from "@/components/ui/feedback";
import { LabeledSelect } from "@/components/ui/select";
import {
	type InfoFlowRow,
	planErrorMessage,
	usePlanInfoFlow,
	usePlanInfoFlowAlerts,
	usePlanToolsOnboarding,
} from "@/lib/api/plan-lab";

import { FailState } from "../kit/fail-state";
import { PageJump } from "../kit/page-jump";
import { type PlanColumn, PlanTable } from "../kit/plan-table";

const ALL = "__all__";

export function InfoFlowTab() {
	const q = usePlanInfoFlow();
	const alerts = usePlanInfoFlowAlerts(3);
	const onboard = usePlanToolsOnboarding();
	const [node, setNode] = useState(ALL);
	const [contract, setContract] = useState(ALL);
	const [gapOnly, setGapOnly] = useState(ALL);
	const data = q.data;

	const rows = useMemo(
		() =>
			(data?.rows ?? []).filter(
				(r) =>
					(node === ALL || r.node === node) &&
					(contract === ALL || r.contract === contract) &&
					(gapOnly === ALL || (gapOnly === "gap" ? !!r.gap : !r.gap)),
			),
		[data, node, contract, gapOnly],
	);

	if (q.isLoading)
		return (
			<Card>
				<CardContent className="py-4">
					<LoadingInline />
				</CardContent>
			</Card>
		);
	if (q.isError || !data)
		return (
			<Card>
				<CardContent>
					<FailState
						reason={planErrorMessage(q.error)}
						onRetry={() => q.refetch()}
						retrying={q.isFetching}
						testId="plan-info-flow-fail"
					/>
				</CardContent>
			</Card>
		);

	const columns: PlanColumn<InfoFlowRow>[] = [
		{
			key: "node",
			title: "节点",
			className: "w-24",
			render: (r) => <span className="text-text-secondary">{data.nodes[r.node] ?? r.node}</span>,
		},
		{
			key: "key",
			title: "输入块",
			className: "w-40",
			render: (r) => (
				<span className="font-mono">
					{r.key}
					{r.sub ? `.${r.sub}` : ""}
				</span>
			),
		},
		{
			key: "what",
			title: "是什么",
			className: "max-w-[220px]",
			render: (r) => (
				<span className="block truncate" title={r.what}>
					{r.what}
				</span>
			),
		},
		{ key: "source", title: "来源", className: "w-32", render: (r) => r.source },
		{
			key: "builder",
			title: "由谁组装",
			className: "max-w-[180px]",
			render: (r) => (
				<span className="block truncate font-mono text-xs" title={r.builder}>
					{r.builder}
				</span>
			),
		},
		{
			key: "when",
			title: "什么时候给",
			className: "max-w-[160px]",
			render: (r) => (
				<span className="block truncate" title={r.guard ? `${r.when}；护栏：${r.guard}` : r.when}>
					{r.when}
					{r.guard ? <span className="text-text-muted">（{r.guard}）</span> : null}
				</span>
			),
		},
		{ key: "contract", title: "字段契约", className: "w-20", render: (r) => r.contract },
		{ key: "step", title: "环节", className: "w-20", render: (r) => r.step },
		{
			key: "gap",
			title: "现状问题",
			className: "max-w-[220px]",
			render: (r) =>
				r.gap ? (
					<span className="block truncate" title={r.gap}>
						{r.gap}
					</span>
				) : (
					<span className="text-text-muted">—</span>
				),
		},
	];
	const alertRows = alerts.data?.rows ?? [];
	const opts = (xs: string[], label: (x: string) => string = (x) => x) => [
		{ value: ALL, label: "全部" },
		...xs.map((x) => ({ value: x, label: label(x) })),
	];

	return (
		<div className="space-y-3">
			{alertRows.length ? (
				<Card data-testid="plan-info-flow-alerts">
					<CardContent className="space-y-2 py-4">
						<div className="text-sm text-text-primary">
							近 {alerts.data?.days} 天有节点输入里出现了没登记的块（会被丢掉，需先登记）
						</div>
						<ul className="space-y-1 text-sm">
							{alertRows.map((a) => (
								<li key={`${a.node}|${a.key}`} className="flex gap-3">
									<span>{data.nodes[a.node] ?? a.node}</span>
									<span className="font-mono">{a.key}</span>
									<span className="text-text-muted">
										{a.count} 次 · 最近 {a.last_seen.slice(0, 16).replace("T", " ")}
									</span>
								</li>
							))}
						</ul>
					</CardContent>
				</Card>
			) : null}
			<Card data-testid="plan-info-flow">
				<CardContent className="space-y-3 py-4">
					<div className="flex flex-wrap items-center gap-2">
						<LabeledSelect
							label="节点"
							value={node}
							onChange={setNode}
							options={opts(Object.keys(data.nodes), (n) => data.nodes[n] ?? n)}
						/>
						<LabeledSelect
							label="字段契约"
							value={contract}
							onChange={setContract}
							options={opts(["是", "部分", "否", "—"])}
						/>
						<LabeledSelect
							label="现状问题"
							value={gapOnly}
							onChange={setGapOnly}
							options={[
								{ value: ALL, label: "全部" },
								{ value: "gap", label: "有问题" },
								{ value: "ok", label: "无" },
							]}
						/>
						<span className="text-xs text-text-muted">
							事实块字段按 <PageJump to="field-contract" /> 投影
						</span>
						<span className="ml-auto text-xs text-text-muted">
							{alerts.isSuccess && !alertRows.length ? "近 3 天没有未登记的输入块 · " : ""}共{" "}
							{rows.length} 块
						</span>
					</div>
					{rows.length ? (
						<PlanTable
							columns={columns}
							rows={rows}
							rowKey={(r) => `${r.node}|${r.key}|${r.sub}`}
							density="compact"
							rowTestId="plan-info-flow-row"
						/>
					) : (
						<EmptyState title="没有符合条件的输入块" desc="换个节点或条件看看。" />
					)}
				</CardContent>
			</Card>
			{onboard.data ? (
				<Card data-testid="plan-tools-onboarding">
					<CardContent className="space-y-2 py-4 text-sm">
						<div className="text-text-primary">
							新工具接入检查：{onboard.data.passed}/{onboard.data.tools} 个工具通过
							<span className="text-text-muted">
								（清单 {onboard.data.items.length} 项，其中
								{onboard.data.items
									.filter((i) => i.manual)
									.map((i) => i.desc.split("：")[0])
									.join("、")}
								需人工核）
							</span>
						</div>
						{onboard.data.rows
							.filter((r) => r.ok === false)
							.map((r) => (
								<div key={`${r.tool}|${r.item}`} className="flex gap-3">
									<span className="font-mono">{r.tool}</span>
									<span>{r.desc.split("：")[0]}</span>
									<span className="text-text-muted">{r.note}</span>
								</div>
							))}
						<div className="text-text-muted">
							登记的证据类型：
							{Object.entries(onboard.data.evidence_kinds)
								.map(([k, v]) => `${k}（${v.split("（")[0]}）`)
								.join("；")}
						</div>
					</CardContent>
				</Card>
			) : null}
		</div>
	);
}
